import {
  Button,
  Loading,
  Video,
  VideoProps,
  WithNativeProps,
  destroyMediaStream,
  getKalyzeeClassNameFromStyleModule,
  useEventListenersRef,
  useRefAutoUpdate,
  useStateWithRef,
} from '@kalyzee/kast-app-web-components';
import { AddEventListernerResult } from '@kalyzee/kast-app-web-components/dist/cjs/services/events';
import { OvenMediaReceiverWebRTCSession, OvenMediaReceiverWebRTCSessionOptions, SdpFormatter } from '@kalyzee/kast-webrtc-client-module';
import React, { ForwardedRef, HTMLProps, useEffect, useImperativeHandle, useRef, useState } from 'react';
import styles from './OvenMediaPlayer.module.css';

const getClassName = (className: string, addInitialClassName = true) => getKalyzeeClassNameFromStyleModule(styles, className, addInitialClassName, 'ovenmediaplayer-');

export interface OvenMediaPlayerListenerMap {
  ovenMediaWebRTCSession: (session: OvenMediaReceiverWebRTCSession | undefined) => void;
  media: (media: MediaStream | undefined) => void;
}

export interface OvenMediaPlayerProps extends WithNativeProps<Partial<HTMLProps<HTMLDivElement>>> {
  url: string;
  sdpFormatterPipeline?: string | string[];
  onOvenMediaSessionOptions?: (options: OvenMediaReceiverWebRTCSessionOptions) => OvenMediaReceiverWebRTCSessionOptions;
  display?: boolean;
  timeoutConnect?: number;
  retryTimeout?: number;
  retryLimit?: number; // < 0 for infinite (ex: -1)
  customContentElement?: React.ReactNode;
  customLoadingElement?: React.ReactNode;
  customErrorElement?: React.ReactNode;
  muted?: boolean;
  callbacks?: Partial<OvenMediaPlayerListenerMap>;
  className?: string;
  style?: React.CSSProperties;
  videoProps?: Partial<Omit<VideoProps, 'src' | 'ref'>>;
}

export interface OvenMediaPlayerRef {
  player: 'ovenmedia';
  retry: () => void;
  getSession: () => OvenMediaReceiverWebRTCSession | undefined;
  addEventListener: <T extends keyof OvenMediaPlayerListenerMap>(
    key: T,
    callback: OvenMediaPlayerListenerMap[T],
  ) => AddEventListernerResult<OvenMediaPlayerListenerMap, T>;
  removeEventListener: <T extends keyof OvenMediaPlayerListenerMap>(key: T, callback: OvenMediaPlayerListenerMap[T]) => void;
}

export const OvenMediaPlayer = React.forwardRef(
  (
    {
      url,
      sdpFormatterPipeline,
      onOvenMediaSessionOptions,
      display = true,
      timeoutConnect = 5000,
      retryTimeout = 3000,
      retryLimit = -1,
      customContentElement,
      customLoadingElement,
      customErrorElement,
      muted = true,
      callbacks,
      className,
      style,
      videoProps,
      ...nativeProps
    }: OvenMediaPlayerProps,
    forwardRef: ForwardedRef<OvenMediaPlayerRef | undefined>,
  ) => {
    const eventListenersRef = useEventListenersRef<keyof OvenMediaPlayerListenerMap, OvenMediaPlayerListenerMap>();
    const callbacksRef = useRefAutoUpdate(callbacks);
    const ovenMediaWebRTCSessionRef = useRef<OvenMediaReceiverWebRTCSession | undefined>(undefined);
    const [retryCounter, setRetryCounter] = useState(0);
    const [error, setError] = useState<string | undefined>(undefined);
    const [previewMedia, setPreviewMedia, previewMediaRef] = useStateWithRef<MediaStream | undefined>(undefined);
    const mediaRef = useRef<MediaStream>();
    const connectedRef = useRef(false);
    const componentIsDestroyedRef = useRef(false);
    const onOvenMediaSessionOptionsRef = useRefAutoUpdate(onOvenMediaSessionOptions);

    const classes: string[] = [getClassName('container')];
    if (className) classes.push(className);

    // ---------------------------------------------------------------------- //
    // ---------------------------   EVENTS ----------------------------- //
    // ---------------------------------------------------------------------- //

    const triggerEvent = <T extends keyof OvenMediaPlayerListenerMap>(key: T, ...args: Parameters<OvenMediaPlayerListenerMap[T]>) => {
      (callbacksRef.current?.[key] as any)?.call(null, ...args);
      eventListenersRef.current?.triggerEvent(key, ...args);
    };

    // ---------------------------------------------------------------------- //
    // ---------------------------  EFFECTS ----------------------------- //
    // ---------------------------------------------------------------------- //

    useImperativeHandle(
      forwardRef,
      () => ({
        player: 'ovenmedia' as const,
        retry: () => setRetryCounter(0),
        getSession: () => ovenMediaWebRTCSessionRef.current,
        addEventListener: <T extends keyof OvenMediaPlayerListenerMap>(key: T, callback: OvenMediaPlayerListenerMap[T]) =>
          eventListenersRef.current?.addEventListener(key, callback),
        removeEventListener: <T extends keyof OvenMediaPlayerListenerMap>(key: T, callback: OvenMediaPlayerListenerMap[T]) =>
          eventListenersRef.current?.removeEventListener(key, callback),
      }),
      [],
    );

    useEffect(() => {
      return () => {
        componentIsDestroyedRef.current = true;
      };
    }, []);

    useEffect(() => {
      return () => {
        if (previewMedia) destroyMediaStream(previewMedia);
      };
    }, [previewMedia]);

    useEffect(() => {
      if (mediaRef.current) destroyMediaStream(mediaRef.current);
      const media = previewMedia?.clone();
      mediaRef.current = media;
      triggerEvent('media', mediaRef.current);
    }, [previewMedia]);

    useEffect(() => {
      if (retryCounter < 0) {
        setRetryCounter(0);
        return;
      }
      const currentTimeoutConnect = timeoutConnect;
      const currRetryLimit = retryLimit;
      const currRetryTimeout = retryTimeout;
      if (currRetryLimit >= 0 && retryCounter >= currRetryLimit) return;
      let session: OvenMediaReceiverWebRTCSession | undefined;
      let retryTimeoutNodeJS: NodeJS.Timeout | undefined;
      let timeoutNodeJS: NodeJS.Timeout | undefined;
      let finished = false;
      const finish = (success: boolean, err?: string) => {
        if (componentIsDestroyedRef.current) return;

        finished = true;
        // console.log('Finished : ', ip, success, err);
        setError(success ? undefined : err ?? 'Unknown');
        if (timeoutNodeJS) clearTimeout(timeoutNodeJS);
        // console.log('Finished #1 : ', connectedRef.current, retryCounter, success);
        if (!success) {
          setPreviewMedia(undefined);
          const newRetryCounter = connectedRef.current ? 0 : retryCounter + 1;
          if (currRetryLimit < 0 || newRetryCounter < currRetryLimit) {
            retryTimeoutNodeJS = setTimeout(() => {
              if (retryCounter === newRetryCounter) setRetryCounter(-1);
              else setRetryCounter(newRetryCounter);
            }, currRetryTimeout);
          } else if (newRetryCounter === currRetryLimit) {
            setRetryCounter(newRetryCounter);
          }
          session?.destroy();
        }
        connectedRef.current = success;
      };
      timeoutNodeJS = setTimeout(() => {
        // console.log("OVENMEDIAPLAYER - timeout");
        if (finished) return;
        finish(false, "Impossible de se connecter");
      }, currentTimeoutConnect);
      setPreviewMedia(undefined);
      try {
        // console.log('Connect to kast : ', ip, retryCounter);
        const options: OvenMediaReceiverWebRTCSessionOptions = {};
        session = new OvenMediaReceiverWebRTCSession(onOvenMediaSessionOptionsRef.current?.(options) ?? options);
        ovenMediaWebRTCSessionRef.current = session;
        session.start(url);
        session.addEventListener('stream', (stream: MediaStream) => {
          if (finished) return;
          finish(true);
          setPreviewMedia(stream);
        });
        session.addEventListener('close', () => {
          // console.log("OVENMEDIAPLAYER - DISCONNECT : ", url);
          finish(false, "Déconnecté");
        });

        const updateSdp = (description: RTCSessionDescriptionInit | null): RTCSessionDescriptionInit | null => {
          if (!description?.sdp) return null;
          if (!sdpFormatterPipeline) return description;
          const pipelines = Array.isArray(sdpFormatterPipeline) ? sdpFormatterPipeline : [sdpFormatterPipeline];
          let result: RTCSessionDescriptionInit = description;
          const sdpFormatter = new SdpFormatter(description.sdp);
          pipelines.forEach((pipeline) => sdpFormatter.execPipeline(pipeline));
          result.sdp = sdpFormatter.sdp;
          return result;
        };

        // Offer
        session.rtcSession.middlewareRemoteSdp = (description: RTCSessionDescriptionInit | null) => {
          return updateSdp(description);
        };

        // Answer
        session.rtcSession.middlewareLocalSdp = (description: RTCSessionDescriptionInit | null) => {
          return updateSdp(description);
        };
        triggerEvent('ovenMediaWebRTCSession', session);
      } catch (err) {
        // console.log("OVENMEDIAPLAYER - error catched : ", err);
        if (finished) return;
        finish(false, (err as any)?.message);
        triggerEvent('ovenMediaWebRTCSession', undefined);
        ovenMediaWebRTCSessionRef.current = undefined;
      }

      return () => {
        if (retryTimeoutNodeJS) clearTimeout(retryTimeoutNodeJS);
        if (timeoutNodeJS) clearTimeout(timeoutNodeJS);
        session?.removeAllEventListeners();
        session?.destroy();
      };
    }, [url, retryCounter, sdpFormatterPipeline, timeoutConnect, retryTimeout, retryLimit]);

    const renderPlayer = () => {
      const currClasses: string[] = [getClassName('player')];
      if (videoProps?.className) currClasses.push(...videoProps.className.split(' '));
      return <Video controls autoPlay muted={muted} {...(videoProps ?? {})} src={previewMedia} className={currClasses.join(' ')} />;
    };

    const renderError = () => (
      <div className={getClassName('errorContainer')}>
        <div className={getClassName('error')}>{error}</div>
        <Button
          className={getClassName('retryButton')}
          title={"Réessayer"}
          onPress={() => {
            setRetryCounter(0);
          }}
        />
      </div>
    );

    const renderLoading = () => <Loading message={"En cours"} />;

    const renderContent = () => {
      if (customContentElement) return customContentElement;
      if (error && retryLimit >= 0 && retryCounter >= retryLimit) return customErrorElement ?? renderError();
      if (previewMedia) return renderPlayer();
      return customLoadingElement ?? renderLoading();
    };

    if (!display) return null;

    return (
      <div className={classes.join(' ')} style={style} {...nativeProps}>
        {renderContent()}
      </div>
    );
  },
);
